import clsx from 'clsx';
import { useCallback } from 'react';

import EditableField from '~components/generic/editable-field/EditableField/EditableField';
import { Button as EditorButton } from '~components/generic/editor/Button/Button';
import { DeleteIcon } from '~components/icons';
import { AnnotationsMarker } from './AnnotationsMarker';
import type { Annotation } from './AnnotationUtils';
import styles from './ModuleAnnotationsStyles.css';

export type AnnotationsListProps = {
  items: Annotation[];
  editing: boolean;
  onChange: (name: string, value: string) => void;
  onDelete: (index: number) => void;
  onMouseOver: (id: string) => void;
  onMouseOut: (id: string) => void;
  onClick: (id: string) => void;
  focusItemId: string;
};

export const AnnotationsList = ({
  items,
  editing,
  onChange,
  onDelete,
  focusItemId,
  onMouseOut,
  onMouseOver,
  onClick,
}: AnnotationsListProps) => {
  const handleDeleteItem = useCallback((index: number) => () => onDelete(index), [onDelete]);
  const handleOnMouseOver = useCallback((id: string) => () => onMouseOver(id), [onMouseOver]);
  const handleOnMouseOut = useCallback((id: string) => () => onMouseOut(id), [onMouseOut]);
  const handleOnClick = useCallback((id: string) => () => onClick(id), [onClick]);

  return (
    <ol className={clsx('c-annotations__items', styles.list)}>
      {items.map((item, idx) => (
        <AnnotationsListItem
          text={item.text}
          index={idx}
          editing={editing}
          onChange={onChange}
          key={item.id}
          onDelete={handleDeleteItem(idx)}
          onMouseOver={handleOnMouseOver(item.id)}
          onMouseOut={handleOnMouseOut(item.id)}
          onClick={handleOnClick(item.id)}
          focus={focusItemId === item.id}
          blur={!!focusItemId && focusItemId !== item.id}
        />
      ))}
    </ol>
  );
};

type AnnotationsListItemProps = {
  index: number;
  text: string;
  editing: boolean;
  onChange: (name: string, value: string) => void;
  onDelete: () => void;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  onClick?: () => void;
  focus: boolean;
  blur: boolean;
};
const AnnotationsListItem = ({
  text,
  index,
  editing,
  onChange,
  onDelete,
  focus,
  blur,
  onMouseOut,
  onMouseOver,
  onClick,
}: AnnotationsListItemProps) => (
  <li
    className={clsx(
      styles.listItem,
      !editing && styles.view,
      !editing && focus && styles.focused,
      !editing && blur && styles.blurred,
      'c-annotations__list-item',
    )}
    onMouseOver={onMouseOver}
    onMouseOut={onMouseOut}
    onClick={onClick}
  >
    <div className={clsx(styles.listItemMarker, 'c-annotations__list-item-marker')}>
      <AnnotationsMarker label={`${index + 1}`} />
    </div>
    <EditableField
      entityType="paragraph--annotations_item"
      fieldName="attributes.field_annotations_item"
      index={index}
      data={text}
      editing={editing}
      onChange={onChange}
      className={editing ? styles.editableField : ''}
    />
    {editing && (
      <div className={styles.btnDeleteContainer}>
        <EditorButton icon={<DeleteIcon />} onClick={onDelete} />
      </div>
    )}
  </li>
);
